import React, { useMemo, useState } from "react";
import { FormikContext, useFormik } from "formik";
import * as Yup from "yup";
import get from "lodash.get";

import { useAuthUpdater } from "../../../../../lib/auth/context/authContext";
import useAuthSession from "../../../../../lib/auth/hooks/useAuthSession";
import { useToastUpdater } from "../../../../../context/toastContext/toastContext";
import { FetchPlusError } from "../../../../../lib/fetchPlus/fetchPlus";
import { SlideProps } from "../../types";
import userService from "../../../../../services/user.service";

import Button from "../../../../common/Buttons/Button/Button";
import Input from "../../../../common/Form/Input/Input";
import Typography from "../../../../common/Typography/Typography";
import FormikFieldError from "../../../../common/Form/Errors/FormikFieldError";
import FormFieldError from "../../../../common/Form/Errors/FormFieldError";

const FormSchema = Yup.object().shape({
    firstname: Yup.string().required("First name is required"),
    lastname: Yup.string().required("Last name is required"),
});

const ConfirmNameForm = ({ nextSlide }: SlideProps) => {
    const [apiErrors, setApiErrors] = useState<string[]>([]);

    const { session } = useAuthSession({ required: true });
    const { refreshSession } = useAuthUpdater();
    const user = session?.user;

    const { addToast } = useToastUpdater();

    const initialValues = useMemo(
        () => ({
            firstname: user?.firstname || "",
            lastname: user?.lastname || "",
        }),
        [user]
    );

    const formikHook = useFormik({
        initialValues: {
            ...initialValues,
        },
        validationSchema: FormSchema,
        onSubmit: async (values, { setSubmitting }) => {
            if (!user?.id) {
                return;
            }

            setApiErrors([]);
            setSubmitting(true);

            try {
                await userService.updateProfile({
                    firstname: values.firstname,
                    lastname: values.lastname,
                    ...(user.phone ? { phone: user.phone } : {}),
                });
                await refreshSession();
                if (nextSlide) {
                    nextSlide();
                }
            } catch (error) {
                const apiErrorArray = Object.values(get(error as FetchPlusError, "response.errors") || {}).reduce(
                    (acc, curr) => acc.concat(curr),
                    []
                );

                setApiErrors(apiErrorArray);

                addToast(`Your Profile settings could not be updated. Please try again.`, "error", "");
                setSubmitting(false);
            }
        },
    });

    const { isSubmitting, handleSubmit, handleChange } = formikHook;

    // setInfo({ status: 'success', title: 'Password reset email sent.', onClose: () => { setInfo(null); } });
    return (
        <div className="space-y-2">
            <Typography as="p" variant="meta">
                Before you get started, let's check all of the details we have stored for you are correct.
            </Typography>
            <FormikContext.Provider value={formikHook}>
                <form className="flex flex-col space-y-2" onSubmit={handleSubmit}>
                    <fieldset className="space-y-1">
                        <Input
                            required
                            id="firstname"
                            name="firstname"
                            label="Your first name"
                            error={!!formikHook.touched.firstname && !!formikHook.errors.lastname}
                            defaultValue={initialValues.firstname}
                            onChange={handleChange}
                        />
                        <FormikFieldError name="firstname" />
                    </fieldset>
                    <fieldset className="space-y-1">
                        <Input
                            required
                            id="lastname"
                            name="lastname"
                            label="Your surname"
                            error={!!formikHook.touched.lastname && !!formikHook.errors.lastname}
                            defaultValue={initialValues.lastname}
                            onChange={handleChange}
                        />
                        <FormikFieldError name="lastname" />
                    </fieldset>

                    {apiErrors.length ? (
                        <ul>
                            {apiErrors.map((error) => (
                                <li key={error}>
                                    <FormFieldError>{error}</FormFieldError>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <div className="flex gap-2 justify-end">
                        <Button type="submit" size="large" icon="arrow_right" loading={isSubmitting}>
                            Confirm these details
                        </Button>
                    </div>
                </form>
            </FormikContext.Provider>
        </div>
    );
};

export default ConfirmNameForm;
